import type {
  FeatureCollection as GeoJSONFeatureCollection,
  Geometry,
} from "geojson";

export type SourceStatus = "red" | "green" | "yellow" | null;
export type OperationalStatus =
  | "registered"
  | "scheduled"
  | "in_progress"
  | "director_review"
  | "completed"
  | "reopened";
export type CommunicationStatus =
  | "not_requested"
  | "requested"
  | "scheduled"
  | "covered"
  | "approved"
  | "rejected";

export interface Territory {
  sectionId: number;
  settlementName?: string;
  settlementKey?: string;
  longitude?: number;
  latitude?: number;
}
export interface Priority {
  id: string;
  periodId: string;
  territoryType: "section" | "settlement";
  territoryKey: string;
  sectionId?: number;
  name: string;
  sourceStatus: SourceStatus;
  authority: "presidency" | "general_secretariat";
  reason: string;
  active: boolean;
  validFrom: string;
  validTo?: string;
  cartographicStatus: "verified" | "pending_reconciliation";
  observations?: string;
  instructionDocumentKey?: string;
  createdAt: string;
  updatedAt: string;
}
export interface PriorityPeriod {
  id: string;
  name: string;
  description?: string;
  startsOn: string;
  endsOn?: string;
  status: "draft" | "active" | "closed";
  assignmentCount: number;
  activeAssignmentCount: number;
  createdAt: string;
  updatedAt: string;
}
export interface PriorityCriterion {
  id: string;
  periodId: string;
  name: string;
  description?: string;
  weight: number;
  indicatorCode?: string;
  active: boolean;
}
export interface PriorityHistory {
  id: string;
  assignmentId: string;
  event: "created" | "updated" | "deleted";
  snapshot: Record<string, unknown>;
  changedBy?: string;
  changedAt: string;
}
export interface Need {
  id: string;
  folio: string;
  origin: "citizen" | "municipal";
  category: string;
  catalogItemId?: string;
  formTemplateId?: string;
  formResponses: Record<string, unknown>;
  description: string;
  area: string;
  status: OperationalStatus;
  territory: Territory;
  createdAt: string;
  clientRequestId?: string;
}
export interface NeedTransfer {
  id: string;
  needId: string;
  fromAreaCode: string;
  fromAreaName: string;
  toAreaCode: string;
  toAreaName: string;
  reason: string;
  transferredAt: string;
}
export interface ActionParticipant {
  id: string;
  actionId: string;
  areaCode: string;
  areaName: string;
  responsibility: string;
  status: "requested" | "accepted" | "declined" | "completed";
  requestedAt: string;
  respondedAt?: string;
}
export interface Stage {
  id: string;
  name: string;
  progress: number;
  notes?: string;
  sentToCommunication: boolean;
  createdAt: string;
}
export interface Action {
  id: string;
  folio: string;
  needId?: string;
  catalogItemId?: string;
  operationKind: OperationKind;
  operationMetadata: Record<string, unknown>;
  title: string;
  area: string;
  status: OperationalStatus;
  communicationStatus: CommunicationStatus;
  progress: number;
  scheduledAt?: string;
  territory: Territory;
  stages: Stage[];
  participants: ActionParticipant[];
  directorConfirmedAt?: string;
  communicationReviewNotes?: string;
  communicationReviewedAt?: string;
  communicationReviewedBy?: string;
}
export interface AgendaItem {
  id: string;
  actionId: string;
  title: string;
  area: string;
  startsAt: string;
  coverageRequested: boolean;
  coverageStatus: CommunicationStatus;
  territory: Territory;
}
export interface Validation {
  id: string;
  actionId: string;
  result: string;
  score?: number;
  comment?: string;
  followUpRequired: boolean;
  createdAt: string;
}
export interface Indicators {
  needsOpen: number;
  actionsActive: number;
  completionRate: number;
  priorityCoverage: number;
  communicatedRate: number;
  directorValidationRate: number;
  citizenSatisfaction: number;
}
export interface Evidence {
  id: string;
  needId?: string;
  actionId?: string;
  stageId?: string;
  evidenceType: "operational" | "communication";
  objectKey: string;
  originalFilename: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
}
export interface BeneficiarySummary {
  actionId: string;
  mode: "aggregate" | "individual";
  aggregateCount: number;
  individualCount: number;
  notes?: string;
  updatedAt: string;
}
export interface Beneficiary {
  id: string;
  actionId: string;
  fullName: string;
  phone?: string;
  email?: string;
  curp?: string;
  address?: string;
  supportDetail?: string;
  consent: boolean;
  createdAt: string;
}
export type IndicatorMetricCode =
  | "needs_open"
  | "actions_active"
  | "actions_completed"
  | "actions_overdue"
  | "completion_rate"
  | "average_attention_hours"
  | "priority_coverage"
  | "evidence_rate"
  | "communicated_rate"
  | "director_validation_rate"
  | "citizen_satisfaction"
  | "reopen_rate"
  | "beneficiary_reach";
export interface IndicatorMetric {
  code: IndicatorMetricCode;
  name: string;
  formula: string;
  unit: "count" | "percent" | "hours" | "score";
}
export interface IndicatorDefinition {
  id: string;
  areaCode: string;
  areaName: string;
  code: string;
  name: string;
  description?: string;
  metricCode: IndicatorMetricCode;
  formula: string;
  unit: "count" | "percent" | "hours" | "score";
  target?: number;
  targetDirection: "at_least" | "at_most";
  periodicity: "daily" | "weekly" | "monthly" | "quarterly" | "annual";
  dimensions: string[];
  active: boolean;
  createdAt: string;
  updatedAt: string;
}
export interface IndicatorResult extends IndicatorDefinition {
  value: number;
  previousValue?: number;
  delta?: number;
  trend: "up" | "down" | "stable" | "new";
  targetMet?: boolean;
  recordCount: number;
}
export interface AuthUser {
  id: string;
  email: string;
  fullName: string;
  active: boolean;
  mustChangePassword: boolean;
  roles: Array<{ code: string; areaCode: string; areaName: string }>;
  permissions: string[];
}
export interface AuthSession {
  authenticated: boolean;
  mode: "demo" | "required";
  user: AuthUser | null;
}
export type NotificationEvent =
  | "action_assigned"
  | "action_due"
  | "communication_coverage"
  | "action_approved";
export type NotificationStatus =
  | "queued"
  | "processing"
  | "retry"
  | "delivered"
  | "failed"
  | "suppressed";
export interface NotificationTemplate {
  eventType: NotificationEvent;
  name: string;
  bodyTemplate: string;
  active: boolean;
  updatedAt: string;
}
export interface NotificationOutboxItem {
  id: string;
  eventType: NotificationEvent;
  recipientUserId: string;
  recipientName?: string;
  entityType: "action" | "agenda";
  entityId: string;
  areaCode?: string;
  renderedText: string;
  linkPath: string;
  status: NotificationStatus;
  attempts: number;
  maxAttempts: number;
  nextAttemptAt: string;
  lastError?: string;
  deliveredAt?: string;
  createdAt: string;
}
export interface NotificationSettings {
  telegram: {
    username?: string;
    verifiedAt?: string;
    active: boolean;
  };
  preferences: Array<{
    eventType: NotificationEvent;
    enabled: boolean;
  }>;
}
export interface TelegramLink {
  expiresInSeconds: number;
  botUsername?: string;
  deepLink?: string;
  code: string;
}
export interface AnalyticsReview {
  status: "accepted" | "rejected" | "dismissed";
  notes?: string;
  reviewerName: string;
  reviewedAt: string;
}
export interface AnalyticsRecommendationBase {
  key: string;
  score: number;
  reasons: string[];
  review?: AnalyticsReview;
}
export interface TerritoryInsight extends AnalyticsRecommendationBase {
  type: "territory_priority";
  sectionId: number;
  settlementKey?: string;
  settlementName?: string;
  priority: boolean;
  needs: number;
  openNeeds: number;
  actions: number;
  overdueActions: number;
  reopenedActions: number;
  averageSatisfaction?: number;
}
export interface DuplicateInsight extends AnalyticsRecommendationBase {
  type: "possible_duplicate";
  first: { id: string; folio: string; category: string; createdAt: string };
  second: { id: string; folio: string; category: string; createdAt: string };
  sectionId: number;
  similarity: number;
}
export interface CoverageInsight extends AnalyticsRecommendationBase {
  type: "communication_coverage";
  actionId: string;
  folio: string;
  title: string;
  sectionId: number;
  settlementName?: string;
  scheduledAt: string;
  priority: boolean;
  operationKind: OperationKind;
}
export interface IndicatorAlertInsight extends AnalyticsRecommendationBase {
  type: "indicator_alert";
  indicatorId: string;
  name: string;
  areaName: string;
  alertKind: "stalled" | "anomaly";
  currentValue: number;
  previousValue: number;
  target?: number;
  unit: "count" | "percent" | "hours" | "score";
}
export type AnalyticsRecommendation =
  | TerritoryInsight
  | DuplicateInsight
  | CoverageInsight
  | IndicatorAlertInsight;
export interface AnalyticsOverview {
  generatedAt: string;
  modelVersion: string;
  windowDays: number;
  summary: { headline: string; bullets: string[] };
  territories: TerritoryInsight[];
  duplicates: DuplicateInsight[];
  coverage: CoverageInsight[];
  indicatorAlerts: IndicatorAlertInsight[];
  methodology: Array<{
    factor: string;
    weight: string;
    explanation: string;
  }>;
  humanReviewRequired: true;
}
export interface DependencyProfile {
  areaCode: string;
  areaName: string;
  shortName: string;
  objective: string;
  primaryOperationKind: OperationKind;
  workflow: string[];
  requirements: string[];
  catalogItems: number;
  publishedForms: number;
  openNeeds: number;
  activeActions: number;
  overdueActions: number;
  pendingCommunication: number;
  readiness: "ready" | "configuration_required";
}
export interface ReadinessStatus {
  status: "ready" | "unavailable";
  checks: { database: boolean; objectStorage: boolean };
}
export interface SystemAlert {
  fingerprint: string;
  alertName: string;
  status: "firing" | "resolved";
  severity: "info" | "warning" | "critical";
  summary: string;
  labels: Record<string, string>;
  startsAt: string;
  endsAt?: string;
  updatedAt: string;
}
export interface AdminCatalogs {
  areas: Array<{ code: string; name: string }>;
  roles: Array<{ code: string; name: string; description?: string }>;
}
export interface RecoveryRequest {
  id: string;
  status: "pending";
  requestedAt: string;
  user: { id: string; email: string; fullName: string };
}
export type OperationKind =
  | "action"
  | "service"
  | "work"
  | "support"
  | "event"
  | "journey"
  | "inspection";
export type FormFieldType =
  | "text"
  | "textarea"
  | "number"
  | "date"
  | "boolean"
  | "select"
  | "photo"
  | "signature"
  | "beneficiary"
  | "location";
export interface CatalogArea {
  code: string;
  name: string;
}
export interface CatalogItem {
  id: string;
  areaCode: string;
  areaName: string;
  operationKind: OperationKind;
  code: string;
  name: string;
  description?: string;
  parentId?: string;
  parentName?: string;
  active: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}
export interface FormField {
  key: string;
  label: string;
  type: FormFieldType;
  required: boolean;
  options?: string[];
  helpText?: string;
}
export interface FormTemplate {
  id: string;
  areaCode: string;
  areaName: string;
  code: string;
  name: string;
  description?: string;
  operationKind: OperationKind;
  version: number;
  status: "draft" | "published" | "retired";
  fields: FormField[];
  publishedAt?: string;
  createdAt: string;
  updatedAt: string;
}
export type FeatureCollection = GeoJSONFeatureCollection<
  Geometry,
  Record<string, unknown>
>;

function cookieValue(name: string) {
  if (typeof document === "undefined") return undefined;
  const row = document.cookie
    .split("; ")
    .find((item) => item.startsWith(`${name}=`));
  return row ? decodeURIComponent(row.slice(name.length + 1)) : undefined;
}
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}
async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? "GET").toUpperCase();
  const csrf = !["GET", "HEAD", "OPTIONS"].includes(method)
    ? cookieValue("siscom_csrf")
    : undefined;
  const response = await fetch(url, {
    ...init,
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      ...(csrf ? { "X-CSRF-Token": csrf } : {}),
      ...init?.headers,
    },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new ApiError(
      body.message ?? `Error ${response.status}`,
      response.status,
      body.error,
    );
  }
  return response.json();
}

export const api = {
  session: () => request<AuthSession>("/api/auth/session"),
  login: (input: { email: string; password: string }) =>
    request<AuthSession>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  logout: () =>
    request<{ authenticated: false }>("/api/auth/logout", {
      method: "POST",
      body: "{}",
    }),
  requestRecovery: (email: string) =>
    request<{ accepted: true; message: string }>("/api/auth/recovery/request", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
  changePassword: (input: { currentPassword: string; newPassword: string }) =>
    request<{ changed: true }>("/api/auth/change-password", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  adminUsers: () => request<AuthUser[]>("/api/admin/users"),
  adminCatalogs: () => request<AdminCatalogs>("/api/admin/catalogs"),
  recoveryRequests: () =>
    request<RecoveryRequest[]>("/api/admin/recovery-requests"),
  createUser: (input: {
    email: string;
    fullName: string;
    roleCode: string;
    areaCode: string;
  }) =>
    request<{ user: AuthUser; temporaryPassword: string }>("/api/admin/users", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  updateUser: (
    userId: string,
    input: { email: string; fullName: string; active: boolean },
  ) =>
    request<AuthUser>(`/api/admin/users/${userId}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  assignRole: (userId: string, input: { roleCode: string; areaCode: string }) =>
    request<AuthUser>(`/api/admin/users/${userId}/role`, {
      method: "PUT",
      body: JSON.stringify(input),
    }),
  resetPassword: (userId: string) =>
    request<{ temporaryPassword: string }>(
      `/api/admin/users/${userId}/reset-password`,
      { method: "POST", body: "{}" },
    ),
  cancelRecovery: (requestId: string) =>
    request<{ cancelled: true }>(
      `/api/admin/recovery-requests/${requestId}/cancel`,
      { method: "POST", body: "{}" },
    ),
  catalogAreas: () => request<CatalogArea[]>("/api/catalogs/areas"),
  catalogItems: (
    filters: {
      areaCode?: string;
      operationKind?: OperationKind;
      includeInactive?: boolean;
    } = {},
  ) =>
    request<CatalogItem[]>(
      `/api/catalogs/items?${new URLSearchParams(
        Object.entries(filters)
          .filter(([, value]) => value !== undefined)
          .map(([key, value]) => [key, String(value)]),
      )}`,
    ),
  createCatalogItem: (input: {
    areaCode: string;
    operationKind: OperationKind;
    code: string;
    name: string;
    description?: string;
    parentId?: string;
    sortOrder: number;
  }) =>
    request<CatalogItem>("/api/catalogs/items", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  updateCatalogItem: (
    itemId: string,
    input: Partial<
      Pick<CatalogItem, "name" | "description" | "active" | "sortOrder">
    >,
  ) =>
    request<CatalogItem>(`/api/catalogs/items/${itemId}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  formTemplates: (
    filters: { areaCode?: string; includeRetired?: boolean } = {},
  ) =>
    request<FormTemplate[]>(
      `/api/catalogs/forms?${new URLSearchParams(
        Object.entries(filters)
          .filter(([, value]) => value !== undefined)
          .map(([key, value]) => [key, String(value)]),
      )}`,
    ),
  createFormTemplate: (input: {
    areaCode: string;
    code: string;
    name: string;
    description?: string;
    operationKind: OperationKind;
    fields: FormField[];
  }) =>
    request<FormTemplate>("/api/catalogs/forms", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  publishFormTemplate: (templateId: string) =>
    request<FormTemplate>(`/api/catalogs/forms/${templateId}/publish`, {
      method: "POST",
      body: "{}",
    }),
  sections: () => request<FeatureCollection>("/api/territory/sections"),
  settlements: () => request<FeatureCollection>("/api/territory/settlements"),
  municipality: () => request<FeatureCollection>("/api/territory/municipality"),
  blocks: (section: number) =>
    request<FeatureCollection>(`/api/territory/blocks?section=${section}`),
  priorities: (periodId?: string, includeInactive = false) =>
    request<Priority[]>(
      `/api/priorities?${new URLSearchParams({ ...(periodId ? { periodId } : {}), ...(includeInactive ? { includeInactive: "true" } : {}) })}`,
    ),
  priorityPeriods: () => request<PriorityPeriod[]>("/api/priority-periods"),
  createPriorityPeriod: (input: {
    name: string;
    description?: string;
    startsOn: string;
    endsOn?: string;
    status: "draft" | "active" | "closed";
  }) =>
    request<PriorityPeriod>("/api/priority-periods", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  updatePriorityPeriod: (
    periodId: string,
    input: Partial<{
      name: string;
      description: string;
      startsOn: string;
      endsOn: string;
      status: "draft" | "active" | "closed";
    }>,
  ) =>
    request<PriorityPeriod>(`/api/priority-periods/${periodId}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  createPriority: (
    input: Omit<Priority, "id" | "sectionId" | "createdAt" | "updatedAt">,
  ) =>
    request<Priority>("/api/priorities", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  updatePriority: (
    assignmentId: string,
    input: Partial<
      Pick<
        Priority,
        | "name"
        | "authority"
        | "reason"
        | "sourceStatus"
        | "active"
        | "validFrom"
        | "validTo"
        | "cartographicStatus"
        | "observations"
        | "instructionDocumentKey"
      >
    >,
  ) =>
    request<Priority>(`/api/priorities/${assignmentId}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  async uploadPriorityDocument(assignmentId: string, file: File) {
    const body = new FormData();
    body.append("file", file);
    const csrf = cookieValue("siscom_csrf");
    const response = await fetch(`/api/priorities/${assignmentId}/document`, {
      method: "POST",
      credentials: "same-origin",
      headers: csrf ? { "X-CSRF-Token": csrf } : {},
      body,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message ?? "No fue posible guardar el documento");
    }
    return response.json() as Promise<{
      objectKey: string;
      filename: string;
      mimeType: string;
      sizeBytes: number;
    }>;
  },
  priorityDocumentUrl: (assignmentId: string) =>
    `/api/priorities/${assignmentId}/document`,
  priorityHistory: (assignmentId: string) =>
    request<PriorityHistory[]>(`/api/priorities/${assignmentId}/history`),
  priorityCriteria: (periodId: string) =>
    request<PriorityCriterion[]>(`/api/priority-periods/${periodId}/criteria`),
  createPriorityCriterion: (
    periodId: string,
    input: {
      name: string;
      description?: string;
      weight: number;
      indicatorCode?: string;
    },
  ) =>
    request<PriorityCriterion>(`/api/priority-periods/${periodId}/criteria`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  needs: () => request<Need[]>("/api/needs"),
  coordinationAreas: () => request<CatalogArea[]>("/api/coordination/areas"),
  needTransfers: (needId: string) =>
    request<NeedTransfer[]>(`/api/needs/${needId}/transfers`),
  transferNeed: (
    needId: string,
    input: { toAreaCode: string; reason: string },
  ) =>
    request<Need>(`/api/needs/${needId}/transfer`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  createNeed: (input: Omit<Need, "id" | "folio" | "status" | "createdAt">) =>
    request<Need>("/api/needs", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  async uploadEvidence(
    file: File,
    association: {
      needId?: string;
      actionId?: string;
      stageId?: string;
      evidenceType?: "operational" | "communication";
    },
  ) {
    const body = new FormData();
    body.append("file", file);
    const query = new URLSearchParams(
      Object.entries(association).filter((entry): entry is [string, string] =>
        Boolean(entry[1]),
      ),
    );
    const csrf = cookieValue("siscom_csrf");
    const response = await fetch(`/api/evidence?${query}`, {
      method: "POST",
      credentials: "same-origin",
      headers: csrf ? { "X-CSRF-Token": csrf } : {},
      body,
    });
    if (!response.ok) throw new Error("No fue posible guardar la fotografía");
    return response.json() as Promise<{
      id: string;
      originalFilename: string;
      mimeType: string;
      sizeBytes: number;
    }>;
  },
  evidence: (
    filters: {
      actionId?: string;
      evidenceType?: "operational" | "communication";
    } = {},
  ) =>
    request<Evidence[]>(
      `/api/evidence?${new URLSearchParams(Object.entries(filters).filter((entry): entry is [string, string] => Boolean(entry[1])))}`,
    ),
  evidenceFileUrl: (evidenceId: string) =>
    `/api/evidence/${encodeURIComponent(evidenceId)}/file`,
  actions: () => request<Action[]>("/api/actions"),
  createDirectAction: (input: {
    catalogItemId: string;
    title: string;
    area: string;
    scheduledAt: string;
    territory: Territory;
    operationMetadata: Record<string, unknown>;
  }) =>
    request<Action>("/api/actions", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  addActionParticipant: (
    actionId: string,
    input: { areaCode: string; responsibility: string },
  ) =>
    request<ActionParticipant>(`/api/actions/${actionId}/participants`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  respondActionParticipant: (
    actionId: string,
    participantId: string,
    status: "accepted" | "declined",
  ) =>
    request<ActionParticipant>(
      `/api/actions/${actionId}/participants/${participantId}/respond`,
      { method: "POST", body: JSON.stringify({ status }) },
    ),
  scheduleNeed: (
    needId: string,
    input: { title: string; scheduledAt: string },
  ) =>
    request<Action>(`/api/needs/${needId}/schedule`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  addStage: (
    actionId: string,
    input: { name: string; progress: number; notes?: string },
  ) =>
    request<Action>(`/api/actions/${actionId}/stages`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  requestCommunication: (actionId: string, stageId?: string) =>
    request<Action>(`/api/actions/${actionId}/communication`, {
      method: "POST",
      body: JSON.stringify({ stageId }),
    }),
  scheduleCommunication: (actionId: string) =>
    request<Action>(`/api/actions/${actionId}/communication/status`, {
      method: "POST",
      body: JSON.stringify({ status: "scheduled" }),
    }),
  reviewCommunication: (
    actionId: string,
    input: { decision: "approved" | "rejected"; notes?: string },
  ) =>
    request<Action>(`/api/actions/${actionId}/communication/review`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  confirmDirector: (actionId: string) =>
    request<Action>(`/api/actions/${actionId}/director-confirm`, {
      method: "POST",
      body: "{}",
    }),
  beneficiarySummary: (actionId: string) =>
    request<BeneficiarySummary>(
      `/api/actions/${actionId}/beneficiaries/summary`,
    ),
  updateBeneficiarySummary: (
    actionId: string,
    input: {
      mode: "aggregate" | "individual";
      aggregateCount: number;
      notes?: string;
    },
  ) =>
    request<BeneficiarySummary>(
      `/api/actions/${actionId}/beneficiaries/summary`,
      { method: "PUT", body: JSON.stringify(input) },
    ),
  beneficiaries: (actionId: string) =>
    request<Beneficiary[]>(`/api/actions/${actionId}/beneficiaries`),
  addBeneficiary: (
    actionId: string,
    input: {
      fullName: string;
      phone?: string;
      email?: string;
      curp?: string;
      address?: string;
      supportDetail?: string;
      consent: boolean;
    },
  ) =>
    request<Beneficiary>(`/api/actions/${actionId}/beneficiaries`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  agenda: () => request<AgendaItem[]>("/api/agenda"),
  validations: () => request<Validation[]>("/api/validations"),
  validate: (
    actionId: string,
    input: {
      result: string;
      score?: number;
      comment?: string;
      followUpRequired: boolean;
    },
  ) =>
    request<Validation>(`/api/actions/${actionId}/validations`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  indicators: () => request<Indicators>("/api/indicators"),
  indicatorMetrics: () => request<IndicatorMetric[]>("/api/indicator-metrics"),
  indicatorDefinitions: (
    filters: { areaCode?: string; includeInactive?: boolean } = {},
  ) =>
    request<IndicatorDefinition[]>(
      `/api/indicator-definitions?${new URLSearchParams(
        Object.entries(filters)
          .filter(([, value]) => value !== undefined)
          .map(([key, value]) => [key, String(value)]),
      )}`,
    ),
  indicatorResults: (
    filters: {
      areaCode?: string;
      sectionId?: number;
      settlementKey?: string;
    } = {},
  ) =>
    request<IndicatorResult[]>(
      `/api/indicator-results?${new URLSearchParams(
        Object.entries(filters)
          .filter(([, value]) => value !== undefined && value !== "")
          .map(([key, value]) => [key, String(value)]),
      )}`,
    ),
  createIndicator: (input: {
    areaCode: string;
    code: string;
    name: string;
    description?: string;
    metricCode: IndicatorMetricCode;
    target?: number;
    targetDirection: "at_least" | "at_most";
    periodicity: IndicatorDefinition["periodicity"];
    dimensions: string[];
  }) =>
    request<IndicatorDefinition>("/api/indicator-definitions", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  updateIndicator: (
    id: string,
    input: Partial<{
      name: string;
      description: string;
      metricCode: IndicatorMetricCode;
      target: number | null;
      targetDirection: "at_least" | "at_most";
      periodicity: IndicatorDefinition["periodicity"];
      dimensions: string[];
      active: boolean;
    }>,
  ) =>
    request<IndicatorDefinition>(`/api/indicator-definitions/${id}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  notificationTemplates: () =>
    request<NotificationTemplate[]>("/api/notifications/templates"),
  updateNotificationTemplate: (
    eventType: NotificationEvent,
    input: Partial<
      Pick<NotificationTemplate, "name" | "bodyTemplate" | "active">
    >,
  ) =>
    request<NotificationTemplate>(`/api/notifications/templates/${eventType}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  notificationOutbox: (
    filters: { all?: boolean; status?: NotificationStatus } = {},
  ) =>
    request<NotificationOutboxItem[]>(
      `/api/notifications/outbox?${new URLSearchParams(
        Object.entries(filters)
          .filter(([, value]) => value !== undefined)
          .map(([key, value]) => [key, String(value)]),
      )}`,
    ),
  notificationSettings: () =>
    request<NotificationSettings>("/api/notifications/settings"),
  updateNotificationPreference: (
    eventType: NotificationEvent,
    enabled: boolean,
  ) =>
    request<{ eventType: NotificationEvent; enabled: boolean }>(
      `/api/notifications/preferences/${eventType}`,
      { method: "PUT", body: JSON.stringify({ enabled }) },
    ),
  createTelegramLink: () =>
    request<TelegramLink>("/api/notifications/telegram/link", {
      method: "POST",
      body: "{}",
    }),
  disconnectTelegram: () =>
    request<{ disconnected: true }>("/api/notifications/telegram", {
      method: "DELETE",
      body: "{}",
    }),
  retryNotification: (id: string) =>
    request<NotificationOutboxItem>(`/api/notifications/outbox/${id}/retry`, {
      method: "POST",
      body: "{}",
    }),
  processNotifications: () =>
    request<{ delivered: number; retried: number; failed: number }>(
      "/api/notifications/process",
      { method: "POST", body: "{}" },
    ),
  analyticsOverview: (
    filters: { areaCode?: string; windowDays?: number } = {},
  ) =>
    request<AnalyticsOverview>(
      `/api/analytics/overview?${new URLSearchParams(
        Object.entries(filters)
          .filter(([, value]) => value !== undefined && value !== "")
          .map(([key, value]) => [key, String(value)]),
      )}`,
    ),
  reviewAnalyticsRecommendation: (
    key: string,
    input: {
      status: AnalyticsReview["status"];
      notes?: string;
      areaCode?: string;
      windowDays: number;
    },
  ) =>
    request<AnalyticsReview>(`/api/analytics/recommendations/${key}/review`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  dependencyProfiles: (areaCode?: string) =>
    request<DependencyProfile[]>(
      `/api/dependencies/profiles${areaCode ? `?areaCode=${encodeURIComponent(areaCode)}` : ""}`,
    ),
  readiness: () => request<ReadinessStatus>("/api/health/ready"),
  systemAlerts: () => request<SystemAlert[]>("/api/monitoring/alerts"),
};
